import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getRoles, createUserRole, updateUserRole } from '../../../api/administration';
import UserRolesHeader from './components/UserRolesHeader';
import UserRolesTable from './components/UserRolesTable';
import UserRolesModal from './components/UserRolesModal';
import PermissionsModal from './components/PermissionsModal';

const UserRoles = () => {
  const initialRole = {
    role: '',
    divison: '',
    description: '',
    access: '',
    access_id: '',
    access_json: '',
    status: 'ACTIVE',
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [currentRole, setCurrentRole] = useState(initialRole);
  const [userRoles, setUserRoles] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [accessDenied, setAccessDenied] = useState(false);

  // Permissions modal state
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [isPermissionsEditMode, setIsPermissionsEditMode] = useState(false);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] = useState(null);

  // Modal handlers
  const openModalForNewRole = () => {
    setIsEditMode(false);
    setEditingRoleId(null);
    setCurrentRole(initialRole);
    setIsModalOpen(true);
  };

  const openModalForEdit = (roleId) => {
    const roleData = userRoles.find(
      (r) => r.role_id === roleId || r.unique_id === roleId
    );
    if (!roleData) {
      toast.error('Role not found');
      return;
    }

    setIsEditMode(true);
    setEditingRoleId(roleData.role_id || roleData.unique_id);
    setCurrentRole({
      role: roleData.role || '',
      divison: roleData.divison || '',
      description: roleData.description || '',
      access: roleData.access || '',
      access_id: roleData.access_id || '',
      access_json: roleData.access_json
        ? (typeof roleData.access_json === 'string'
            ? roleData.access_json
            : JSON.stringify(roleData.access_json, null, 2))
        : '',
      status: roleData.status || 'ACTIVE',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingRoleId(null);
    setCurrentRole(initialRole);
  };

  // Permissions modal handlers
  const handleNewPermission = () => {
    setIsPermissionsEditMode(false);
    setSelectedRoleForPermissions(null);
    setIsPermissionsModalOpen(true);
  };

  const handleEditPermission = () => {
    setIsPermissionsEditMode(true);
    setSelectedRoleForPermissions(null);
    setIsPermissionsModalOpen(true);
  };

  const closePermissionsModal = () => {
    setIsPermissionsModalOpen(false);
    setIsPermissionsEditMode(false);
    setSelectedRoleForPermissions(null);
  };

  // Handle permissions form submission
  const handlePermissionsSubmit = async (formData) => {
    try {
      setSubmitting(true);
      // Permissions data prepared for save

      // Find the role to update
      const roleToUpdate = userRoles.find(
        (r) => r.role_id === formData.role_id || r.unique_id === formData.role_id
      );

      if (!roleToUpdate) {
        toast.error('Role not found');
        return;
      }

      // Update the role with new permissions
      const updatedRoleData = {
        ...roleToUpdate,
        access_json: formData.access_json,
      };

      await updateUserRole(formData.role_id, updatedRoleData);
      toast.success('Permissions saved successfully');
      closePermissionsModal();
      getData();
    } catch (error) {
      console.error('Error saving permissions:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 403) {
        toast.error('Access denied: You do not have permission to manage permissions');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else {
        toast.error(errorMessage || 'Failed to save permissions');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Form submission
  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      // Parse access_json if it's a string
      let accessJson = null;
      if (formData.access_json) {
        try {
          accessJson = typeof formData.access_json === 'string'
            ? JSON.parse(formData.access_json)
            : formData.access_json;
        } catch (e) {
          console.warn('Invalid JSON in access_json field');
        }
      }

      // Build payload per API spec (role, description, status, access_id only)
      const basePayload = {
        role: formData.role?.trim(),
        description: formData.description?.trim() || '',
        status: formData.status,
        ...(formData.access_id && { access_id: parseInt(formData.access_id, 10) }),
        ...(accessJson && { access_json: accessJson }),
      };

      if (isEditMode) {
        await updateUserRole(editingRoleId, basePayload);
        toast.success('Role Updated Successfully');
      } else {
        await createUserRole(basePayload);
        toast.success('Role Created Successfully');
      }

      closeModal();
      getData();
    } catch (error) {
      console.error('Error saving role:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      // Check for network errors
      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        toast.error('Access denied: You do not have permission to manage roles');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 400) {
        toast.error(errorMessage || 'Invalid data. Please check your input');
      } else {
        toast.error(errorMessage || 'Failed to save role');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Delete handler
  const handleDeleteRole = () => {
    // TODO: Implement delete confirmation and API call
    toast.info('Delete feature coming soon');
  };

  // Fetch data
  const getData = async () => {
    setLoading(true);
    try {
      const response = await getRoles();
      // User roles data fetched

      if (response && response.data) {
        const data = response.data;
        const rolesList = data.data?.records || data.records || [];
        // Roles list extracted
        setUserRoles(Array.isArray(rolesList) ? rolesList : []);
      } else if (Array.isArray(response)) {
        setUserRoles(response);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      // Check for 403 Forbidden - show on UI instead of toast
      if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        setAccessDenied(true);
      } else if (error.message === 'Network Error' || !error.response) {
        // Network errors - also show access denied with different handling
        setAccessDenied(true);
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error(errorMessage || 'Failed to load user roles');
      }
      setUserRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Search handler
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Page size handler
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter roles based on search term
  const filteredRoles = userRoles.filter((role) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      role.role?.toLowerCase().includes(searchLower) ||
      role.divison?.toLowerCase().includes(searchLower) ||
      role.description?.toLowerCase().includes(searchLower) ||
      role.status?.toLowerCase().includes(searchLower)
    );
  });

  // Show access denied screen
  if (accessDenied) {
    return (
      <div className="px-5">
        <div className="border border-gray-300 mt-5 rounded-lg">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
            <p className="text-gray-500 text-center max-w-md">
              You are not authorized to manage roles. Please contact your Admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5">
      <div className="border border-gray-300 mt-5 rounded-lg relative">
        {/* Header */}
        <UserRolesHeader
          onReload={getData}
          onNewPermission={handleNewPermission}
          onEditPermission={handleEditPermission}
          onAddUserRole={openModalForNewRole}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <UserRolesTable
            data={filteredRoles}
            onEditRole={openModalForEdit}
            onDeleteRole={handleDeleteRole}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* User Role Modal */}
      <UserRolesModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentRole}
        isSubmitting={isSubmitting}
      />

      {/* Permissions Modal */}
      <PermissionsModal
        isOpen={isPermissionsModalOpen}
        onClose={closePermissionsModal}
        onSubmit={handlePermissionsSubmit}
        isEditMode={isPermissionsEditMode}
        defaultValues={selectedRoleForPermissions}
        roleOptions={userRoles}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default UserRoles;
